<script setup>
import { ref, onMounted } from 'vue';

import { useAtlasConnectorAdmin, HOST_PRESETS } from '@/composables/useAtlasConnectorAdmin';
import { useNotification } from '@/composables/useNotification';

import Notification from '@/components/common/SnackBar.vue';

const { notification, notify } = useNotification();
const admin = useAtlasConnectorAdmin();

// Expanded-row state is Vuetify-specific, so it stays here rather than in the shared composable.
const expanded = ref([]);

const headers = [
	{ text: '', value: 'data-table-expand', width: '40px' },
	{ text: 'Customer', value: 'client_name' },
	{ text: 'Tenant ID', value: 'client_id', width: '100px' },
	{ text: 'Type', value: 'client_type', width: '120px' },
	{ text: 'Connectors', value: 'active_connectors', width: '120px', align: 'center' },
	{ text: 'Tokens', value: 'active_tokens', width: '100px', align: 'center' },
	{ text: 'Last used', value: 'last_used', width: '170px' },
	{ text: '', value: 'actions', sortable: false, width: '180px', align: 'end' },
];

function handleRefresh() {
	admin.fetchData(notify);
}
function handleCopy(text) {
	admin.copy(text, notify);
}
function handleSubmitCreate() {
	admin.submitCreate(notify);
}
function handleSubmitMintToken() {
	admin.submitMintToken(notify);
}
function handleSubmitEditRedirects() {
	admin.submitEditRedirects(notify);
}
function handleRevokeClient(tenant, client) {
	admin.revokeClient(tenant, client, notify);
}
function handleRevokeToken(tenant, token) {
	admin.revokeToken(tenant, token, notify);
}

onMounted(() => {
	admin.fetchData(notify);
});
</script>

<template>
	<div class="atlas-connector-admin">
		<Notification :notification="notification" />

		<div class="page-header pa-4 pb-0">
			<div class="d-flex align-center mb-2">
				<h2>Atlas MCP Connectors</h2>
				<v-spacer />
				<v-btn small outlined :loading="admin.loading.value" @click="handleRefresh">
					<v-icon left small>mdi-refresh</v-icon>
					Refresh
				</v-btn>
			</div>
			<div class="caption grey--text mb-3">
				One row per tenant. A <b>connector</b> is an OAuth client an MCP host (Copilot Studio, Claude, …) uses to sign
				users into Atlas; a <b>connector token</b> is the one-shot secret that authorises registering one.
				<b>Create connector</b> does both steps and hands back a client ID / secret for Manual OAuth setup.
				<span v-if="admin.issuer.value">Issuer: <code>{{ admin.issuer.value }}</code></span>
				<span v-else class="red--text">OAUTH_ISSUER is not set on rm-api — registration will fail.</span>
			</div>

			<v-row dense align="center" class="mb-2">
				<v-col cols="auto" style="min-width: 260px;">
					<v-text-field v-model="admin.searchText.value" label="Search customer…" outlined dense hide-details clearable
						prepend-inner-icon="mdi-magnify" />
				</v-col>
				<v-col cols="auto">
					<v-switch v-model="admin.onlyWithConnectors.value" label="Only tenants with records" dense hide-details class="mt-0 pt-0" />
				</v-col>
				<v-spacer />
				<v-col cols="auto" class="caption grey--text">
					{{ admin.rows.value.length }} tenant(s) · {{ admin.totalConnectors.value }} active connector(s)
				</v-col>
			</v-row>
		</div>

		<div class="page-body px-4 pb-4">
			<v-data-table :headers="headers" :items="admin.rows.value" :loading="admin.loading.value" item-key="client_id"
				:expanded.sync="expanded" show-expand single-expand :items-per-page="50" dense
				:footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }">

				<template v-slot:[`item.client_name`]="{ item }">
					<span class="font-weight-medium">{{ item.client_name }}</span>
					<v-chip v-if="item.client_type === 'INTERNAL'" x-small class="ml-2" color="deep-purple lighten-4">staff connector</v-chip>
				</template>

				<template v-slot:[`item.client_type`]="{ item }">
					<span class="caption">{{ item.client_type }}</span>
				</template>

				<template v-slot:[`item.active_connectors`]="{ item }">
					<v-chip x-small :color="item.active_connectors ? 'green lighten-4' : 'grey lighten-3'">{{ item.active_connectors }}</v-chip>
				</template>

				<template v-slot:[`item.active_tokens`]="{ item }">
					<v-chip x-small :color="item.active_tokens ? 'amber lighten-4' : 'grey lighten-3'">{{ item.active_tokens }}</v-chip>
				</template>

				<template v-slot:[`item.last_used`]="{ item }">
					<span class="caption">{{ admin.fmtDate(item.last_used) }}</span>
				</template>

				<template v-slot:[`item.actions`]="{ item }">
					<v-btn x-small :color="item.has_records ? undefined : 'primary'" :outlined="item.has_records" :dark="!item.has_records"
						class="mr-1" @click.stop="admin.openCreate(item)">
						<v-icon x-small left>mdi-key-plus</v-icon>
						{{ item.has_records ? 'Add connector' : 'Create connector' }}
					</v-btn>
					<v-btn x-small icon title="Mint a raw connector token (for hosts that support dynamic registration)"
						@click.stop="admin.openMintToken(item)">
						<v-icon small>mdi-ticket-confirmation-outline</v-icon>
					</v-btn>
				</template>

				<!-- Expanded: connectors + tokens for this tenant -->
				<template v-slot:expanded-item="{ headers, item }">
					<td :colspan="headers.length" class="expanded-cell pa-3">
						<div class="subtitle-2 mb-1">Connectors (OAuth clients)</div>
						<div v-if="!item.connectors.length" class="caption grey--text mb-3">
							No connectors registered for this tenant yet.
						</div>
						<v-simple-table v-else dense class="mb-3 inner-table">
							<thead>
								<tr>
									<th>Name</th>
									<th>Client ID</th>
									<th>Auth</th>
									<th>Redirect URIs</th>
									<th>Scope</th>
									<th>Registered</th>
									<th>Last used</th>
									<th>Status</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="c in item.connectors" :key="c.client_id" :class="{ 'revoked-row': c.revoked }">
									<td>{{ c.client_name }}</td>
									<td>
										<code :title="c.client_id">{{ admin.shortId(c.client_id) }}</code>
										<v-btn x-small icon @click="handleCopy(c.client_id)"><v-icon x-small>mdi-content-copy</v-icon></v-btn>
									</td>
									<td class="caption">{{ admin.authMethodLabel(c.token_endpoint_auth_method) }}</td>
									<td class="caption">
										<div v-for="u in c.redirect_uris" :key="u" class="uri">{{ u }}</div>
									</td>
									<td class="caption">{{ c.scope || '—' }}</td>
									<td class="caption">{{ admin.fmtDate(c.registered_at) }}</td>
									<td class="caption">{{ admin.fmtDate(c.last_used) }}</td>
									<td>
										<v-chip x-small :color="c.revoked ? 'grey' : 'green'" dark>{{ c.revoked ? `Revoked${c.revoked_reason ? ' · ' + c.revoked_reason : ''}` : 'Active' }}</v-chip>
									</td>
									<td class="text-right text-no-wrap">
										<v-btn v-if="!c.revoked" x-small text color="primary" title="Edit redirect URIs (client ID and secret stay the same)"
											@click="admin.openEditRedirects(item, c)">
											<v-icon x-small left>mdi-pencil</v-icon>Redirects
										</v-btn>
										<v-btn v-if="!c.revoked" x-small text color="error" @click="handleRevokeClient(item, c)">Revoke</v-btn>
									</td>
								</tr>
							</tbody>
						</v-simple-table>

						<div class="subtitle-2 mb-1">Connector tokens</div>
						<div v-if="!item.tokens.length" class="caption grey--text">No connector tokens minted for this tenant.</div>
						<v-simple-table v-else dense class="inner-table">
							<thead>
								<tr>
									<th>Label</th>
									<th>Issued by</th>
									<th>Created</th>
									<th>Expires</th>
									<th>Scope template</th>
									<th>State</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="k in item.tokens" :key="k.token_hash">
									<td>{{ k.label || '—' }} <code class="caption grey--text">{{ k.token_hash.slice(0, 10) }}</code></td>
									<td class="caption">{{ k.issued_by_name || k.issued_by_user_id }}</td>
									<td class="caption">{{ admin.fmtDate(k.created) }}</td>
									<td class="caption">{{ admin.fmtDate(k.expires) }}</td>
									<td class="caption">{{ k.scope_template || '(all)' }}</td>
									<td>
										<v-chip x-small dark :color="admin.tokenState(k).color">{{ admin.tokenState(k).text }}</v-chip>
									</td>
									<td class="text-right">
										<v-btn v-if="admin.tokenState(k).live" x-small text color="error"
											@click="handleRevokeToken(item, k)">Revoke</v-btn>
									</td>
								</tr>
							</tbody>
						</v-simple-table>
					</td>
				</template>
			</v-data-table>
		</div>

		<!-- ── Create connector dialog (mint token + register client) ── -->
		<v-dialog v-model="admin.createDialog.show" max-width="760" persistent>
			<v-card v-if="admin.createDialog.tenant">
				<v-card-title class="headline">
					<span v-if="!admin.createDialog.result">Create connector · {{ admin.createDialog.tenant.client_name }}</span>
					<span v-else><v-icon color="success" class="mr-2">mdi-check-circle</v-icon>Connector registered</span>
					<v-spacer />
					<v-btn icon @click="admin.closeCreate()"><v-icon>mdi-close</v-icon></v-btn>
				</v-card-title>

				<!-- Step 1: form -->
				<v-card-text v-if="!admin.createDialog.result">
					<div class="caption grey--text mb-3">
						Mints a single-use connector token for tenant <b>{{ admin.createDialog.tenant.client_id }}</b> and registers an
						OAuth client through the Atlas MCP server with it. Confidential clients get a secret — shown once.
					</div>

					<v-select :value="admin.createDialog.preset" :items="HOST_PRESETS" item-text="text" item-value="key"
						label="MCP host" outlined dense class="mb-2" @change="admin.applyPreset" />

					<v-text-field v-model="admin.createDialog.client_name" label="Connector name" outlined dense class="mb-2"
						hint="Shown in the client list; e.g. the team or host it is for" persistent-hint />

					<v-textarea v-model="admin.createDialog.redirect_uris_text" label="Redirect URIs (one per line)" outlined dense rows="3"
						class="mb-2" hint="Must match the host's OAuth callback exactly. The Microsoft preset covers all three Microsoft surfaces — leave every line in place unless you know the customer uses only one. A trailing /* is a path wildcard, honoured only on trusted hosts." persistent-hint />

					<v-text-field v-model="admin.createDialog.scope" label="Scopes" outlined dense class="mb-2" />

					<v-switch v-model="admin.createDialog.confidential" dense hide-details class="mt-0"
						:label="admin.createDialog.confidential ? 'Confidential client — generate a client secret (Copilot Studio Manual mode)' : 'Public client — PKCE, no secret (Claude)'" />

					<v-alert v-if="admin.createDialog.error" type="error" dense text class="mt-3 mb-0">{{ admin.createDialog.error }}</v-alert>
				</v-card-text>

				<!-- Step 2: result (secret shown once) -->
				<v-card-text v-else>
					<v-alert type="warning" dense text class="mb-3">
						Copy these now. The client secret is <b>not stored in clear</b> and cannot be shown again — revoke and re-create if lost.
					</v-alert>

					<div class="result-grid">
						<template v-for="row in admin.resultRows.value">
							<div :key="row.k + '-k'" class="result-key">{{ row.k }}</div>
							<div :key="row.k + '-v'" class="result-val">
								<code :class="{ 'secret-val': row.secret }">{{ row.v }}</code>
								<v-btn x-small icon @click="handleCopy(row.v)"><v-icon x-small>mdi-content-copy</v-icon></v-btn>
							</div>
						</template>
					</div>

					<div class="caption grey--text mt-3">
						<span v-if="admin.createDialog.result.client_secret">
							In Copilot Studio choose <b>OAuth 2.0 → Manual</b> and paste the values above. The token endpoint accepts the
							secret as <code>client_secret_post</code>.
						</span>
						<span v-else>
							Public client: the host uses PKCE. Enter the Client ID where the host asks for an OAuth client ID.
						</span>
					</div>
				</v-card-text>

				<v-card-actions>
					<v-spacer />
					<template v-if="!admin.createDialog.result">
						<v-btn text @click="admin.closeCreate()">Cancel</v-btn>
						<v-btn color="primary" :loading="admin.createDialog.busy" :disabled="!admin.canCreate.value" @click="handleSubmitCreate">
							<v-icon left small>mdi-key-plus</v-icon>
							Mint token &amp; register
						</v-btn>
					</template>
					<v-btn v-else color="primary" @click="admin.closeCreate()">Done</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- ── Edit redirect URIs dialog ── -->
		<v-dialog v-model="admin.editDialog.show" max-width="640" persistent>
			<v-card v-if="admin.editDialog.client">
				<v-card-title class="headline">
					Edit redirect URIs · {{ admin.editDialog.client.client_name }}
					<v-spacer />
					<v-btn icon @click="admin.editDialog.show = false"><v-icon>mdi-close</v-icon></v-btn>
				</v-card-title>
				<v-card-text>
					<div class="caption grey--text mb-3">
						Client <code>{{ admin.editDialog.client.client_id }}</code> keeps its ID and secret. Takes effect on the next
						sign-in. If the host reported <code>invalid_redirect_uri</code>, copy the full authorize URL from the popup's
						address bar and paste the decoded <code>redirect_uri</code> value here, exactly as sent.
					</div>
					<v-textarea v-model="admin.editDialog.redirect_uris_text" label="Redirect URIs (one per line)" outlined dense rows="4"
						hint="https URLs, or claude: / cursor: / vscode: / mcp: schemes. A trailing /* is a path wildcard, honoured only on trusted hosts such as global.consent.azure-apim.net." persistent-hint />
					<v-alert v-if="admin.editDialog.error" type="error" dense text class="mt-3 mb-0">{{ admin.editDialog.error }}</v-alert>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="admin.editDialog.show = false">Cancel</v-btn>
					<v-btn color="primary" :loading="admin.editDialog.busy" :disabled="!admin.parsedEditRedirectUris.value.length"
						@click="handleSubmitEditRedirects">Save</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- ── Mint raw connector token dialog ── -->
		<v-dialog v-model="admin.tokenDialog.show" max-width="560" persistent>
			<v-card v-if="admin.tokenDialog.tenant">
				<v-card-title class="headline">
					Mint connector token · {{ admin.tokenDialog.tenant.client_name }}
					<v-spacer />
					<v-btn icon @click="admin.tokenDialog.show = false"><v-icon>mdi-close</v-icon></v-btn>
				</v-card-title>

				<v-card-text v-if="!admin.tokenDialog.result">
					<div class="caption grey--text mb-3">
						For MCP hosts that support dynamic client registration with an initial access token, or for manual
						<code>curl</code> registration. The raw token is shown once.
					</div>
					<v-text-field v-model="admin.tokenDialog.label" label="Label" outlined dense class="mb-2" />
					<v-row dense>
						<v-col cols="6"><v-text-field v-model="admin.tokenDialog.usesRemaining" type="number" min="1" label="Uses" outlined dense /></v-col>
						<v-col cols="6"><v-text-field v-model="admin.tokenDialog.expiresInHours" type="number" min="1" label="Expires in (hours)" outlined dense /></v-col>
					</v-row>
					<v-alert v-if="admin.tokenDialog.error" type="error" dense text class="mb-0">{{ admin.tokenDialog.error }}</v-alert>
				</v-card-text>

				<v-card-text v-else>
					<v-alert type="warning" dense text class="mb-3">Copy this now — it is not shown again.</v-alert>
					<div class="result-grid">
						<div class="result-key">Connector token</div>
						<div class="result-val">
							<code class="secret-val">{{ admin.tokenDialog.result.token }}</code>
							<v-btn x-small icon @click="handleCopy(admin.tokenDialog.result.token)"><v-icon x-small>mdi-content-copy</v-icon></v-btn>
						</div>
						<div class="result-key">Expires</div>
						<div class="result-val"><code>{{ admin.fmtDate(admin.tokenDialog.result.expires) }}</code></div>
						<div class="result-key">Register endpoint</div>
						<div class="result-val"><code>POST {{ admin.issuer.value }}/oauth/register</code> with <code>Authorization: Bearer &lt;token&gt;</code></div>
					</div>
				</v-card-text>

				<v-card-actions>
					<v-spacer />
					<template v-if="!admin.tokenDialog.result">
						<v-btn text @click="admin.tokenDialog.show = false">Cancel</v-btn>
						<v-btn color="primary" :loading="admin.tokenDialog.busy" @click="handleSubmitMintToken">Mint token</v-btn>
					</template>
					<v-btn v-else color="primary" @click="admin.tokenDialog.show = false">Done</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<style scoped>
.atlas-connector-admin {
	display: flex;
	flex-direction: column;
	height: 100%;
}
.expanded-cell {
	background: #fafafa;
}
.inner-table {
	background: transparent;
}
.revoked-row {
	opacity: 0.55;
}
.uri {
	word-break: break-all;
}
.result-grid {
	display: grid;
	grid-template-columns: 150px 1fr;
	gap: 6px 12px;
	align-items: start;
}
.result-key {
	font-weight: 500;
	font-size: 13px;
	padding-top: 3px;
}
.result-val code {
	white-space: pre-wrap;
	word-break: break-all;
	font-size: 12px;
}
.secret-val {
	background: #fff8e1;
}
</style>
